/**
 * admin-ops-api の Workspace 詳細 (OPS-DR-1〜4) 用 fetch wrapper。
 * sales_counter 単体前提へ縮退（#1479、カウンターシリーズ4製品構想は2026-07-02撤退）。
 *
 * 設計: spec/ai-inbox-core/admin-ops/requirements.md §4.4 / design.md §5.2
 *
 * 守るライン:
 *   - API レスポンスに PII が含まれない（API 側でマスキング済）
 *   - Stripe Customer Portal リンクは外部リンク（書き込み API は呼ばない）
 */
import type { BillingStatus, OwnerProductEmailConsent } from './workspaces-api';

export interface WorkspaceConnections {
  aiEnabled: boolean;
  emailConnected: boolean;
  salesAiToggle: boolean | null;
  shopifyConnected: boolean | null;
  shopifyCustomAppState: 'installed' | 'pending' | null;
  lineConnected: boolean | null;
  mapboxConnected: boolean | null;
  inquiryFormPublished: boolean | null;
}

export interface WorkspaceBasicInfo {
  workspaceId: string;
  displayName: string;
  slug: string;
  createdAt: string;
  plan: string | null;
  billingStatus: BillingStatus;
  stripeCustomerIdMasked: string | null;
  stripeCustomerPortalUrl: string | null;
  onboardingProgress: number;
  /** #1886: Owner の「製品に関するお知らせメール」の許可状況 */
  ownerProductEmailConsent: OwnerProductEmailConsent;
  ownerProductEmailConsentChangedAt: string | null;
  connections: WorkspaceConnections;
}

export interface UsageWindow {
  windowDays: 7 | 30 | 90;
  leadsCount: number;
  aiResultsCount: number;
  aiAdoptionRate: number | null;
  activeUsersCount: number;
}

export interface UsagePanel {
  windows: UsageWindow[];
}

export interface MaskedAuditLogRow {
  id: string;
  workspaceId: string | null;
  actorType: string;
  actorId: string | null;
  actorRole: string | null;
  actionName: string;
  targetType: string;
  targetId: string | null;
  reason: string | null;
  beforeValue: unknown;
  afterValue: unknown;
  ipAddressHash: string | null;
  userAgent: string | null;
  occurredAt: string;
}

export interface RecentActivity {
  rows: MaskedAuditLogRow[];
  fetched: number;
}

export interface BillingHistoryEntry {
  status: BillingStatus;
  enteredAt: string;
  endedAt: string | null;
  reason: string | null;
  /**
   * この区間の出どころ（#1863）。
   *   - `audit`: Stripe webhook の監査ログに残った実際の遷移
   *   - `approximate`: 監査ログが無い期間を subscriptions の日時から組み立てた概算
   */
  source: 'audit' | 'approximate';
}

export interface BillingHistory {
  currentStatus: BillingStatus;
  timeline: BillingHistoryEntry[];
  /** ここから先は実際の遷移（#1863）。null = 全区間が概算。 */
  accurateSince: string | null;
  stripeCustomerPortalUrl: string | null;
}

export interface WorkspaceSummaryResult {
  configured: true;
  basic: WorkspaceBasicInfo;
  usage: UsagePanel;
  activity: RecentActivity;
  billing: BillingHistory;
}

export interface WorkspaceSummaryUnconfigured {
  configured: false;
  workspaceId: string;
  note?: string;
}

export interface WorkspaceSummaryNotFound {
  configured: true;
  error: 'workspace_not_found';
  workspaceId: string;
}

export type WorkspaceSummaryResponse =
  | WorkspaceSummaryResult
  | WorkspaceSummaryUnconfigured
  | WorkspaceSummaryNotFound;

function trimUrl(baseUrl: string): string {
  return baseUrl.replace(/\/$/, '');
}

export async function fetchWorkspaceSummary(
  baseUrl: string,
  workspaceId: string,
): Promise<WorkspaceSummaryResponse> {
  const res = await fetch(
    `${trimUrl(baseUrl)}/api/v1/workspaces/${encodeURIComponent(workspaceId)}/summary`,
    {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    },
  );
  if (res.status === 404) {
    return (await res.json()) as WorkspaceSummaryNotFound;
  }
  if (!res.ok) {
    throw new Error(`admin-ops API /workspaces/:id/summary failed: ${res.status}`);
  }
  return (await res.json()) as WorkspaceSummaryResponse;
}

// ===================================================================
// #1300 機能フラグ制御面（軸A = alys_owner）
//
// admin-ops-api（#1299）の GET/PUT/DELETE `/workspaces/:id/feature-flags` を
// 消費する薄い fetch wrapper。制御面は **課金・プラン・価格を一切参照しない**
// （守るライン #2）。開放/閉鎖は運用上の段階開放トグルであって paywall ではない。
//
// 設計: spec/sales-counter/changes/2026-06-28-feature-flags.md §「UI 差分 / 制御面」
//   / screen-contracts/admin-ops-workspace-feature-flags.md
//   / state-matrices/admin-ops-workspace-feature-flags.md
// ===================================================================

/** GET レスポンスの 1 フラグ行（registry メタ + effective / override 状態）。 */
export interface FeatureFlagListItem {
  key: string;
  label: string;
  description: string;
  /** registry 既定値。 */
  defaultEnabled: boolean;
  /** 有効値（registry 既定 → 上書きで解決した最終値）。 */
  effective: boolean;
  /** 上書き行があるか（false = registry 既定を使用）。 */
  overridden: boolean;
  /** 上書き値（上書き行が無ければ null）。 */
  overrideEnabled: boolean | null;
  reason: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
}

export interface FeatureFlagsResult {
  configured: true;
  workspaceId: string;
  /** 切り替えができるか（書込接続あり）。false = 読み取り専用接続での閲覧のみ（#1871）。 */
  writable: boolean;
  flags: FeatureFlagListItem[];
}

/** admin-ops のサーバ側の読み取り接続・書込接続がどちらも未配線（Founder 手作業待ち）。 */
export interface FeatureFlagsUnconfigured {
  configured: false;
  workspaceId: string;
}

export interface FeatureFlagsNotFound {
  configured: true;
  error: 'workspace_not_found';
  workspaceId: string;
}

export type FeatureFlagsResponse =
  | FeatureFlagsResult
  | FeatureFlagsUnconfigured
  | FeatureFlagsNotFound;

/** トグル / リセット成功レスポンス（key の effective 値を返す）。 */
export interface FeatureFlagMutationResult {
  configured: true;
  workspaceId: string;
  key: string;
  effective: boolean;
}

/**
 * admin-ops feature-flags API のエラー（status 付き）。
 * status===403 のとき UI は「権限がありません」を示す（State Matrix L5 / X7）。
 */
export class FeatureFlagApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'FeatureFlagApiError';
    this.status = status;
  }
}

/** GET: 当該テナントの registry フラグ一覧 + 各 effective / override 状態。 */
export async function fetchWorkspaceFeatureFlags(
  baseUrl: string,
  workspaceId: string,
): Promise<FeatureFlagsResponse> {
  const res = await fetch(
    `${trimUrl(baseUrl)}/api/v1/workspaces/${encodeURIComponent(workspaceId)}/feature-flags`,
    { credentials: 'include', headers: { Accept: 'application/json' } },
  );
  if (res.status === 403) {
    throw new FeatureFlagApiError(403, 'forbidden');
  }
  if (res.status === 404) {
    return (await res.json()) as FeatureFlagsNotFound;
  }
  if (!res.ok) {
    throw new FeatureFlagApiError(
      res.status,
      `admin-ops API /workspaces/:id/feature-flags failed: ${res.status}`,
    );
  }
  return (await res.json()) as FeatureFlagsResponse;
}

/** PUT: 上書きを upsert（enabled=true → 開放 / false → 閉鎖）+ 監査。 */
export async function putWorkspaceFeatureFlag(
  baseUrl: string,
  workspaceId: string,
  key: string,
  enabled: boolean,
): Promise<FeatureFlagMutationResult> {
  const res = await fetch(
    `${trimUrl(baseUrl)}/api/v1/workspaces/${encodeURIComponent(workspaceId)}/feature-flags/${encodeURIComponent(key)}`,
    {
      method: 'PUT',
      credentials: 'include',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    },
  );
  if (res.status === 403) {
    throw new FeatureFlagApiError(403, 'forbidden');
  }
  if (!res.ok) {
    throw new FeatureFlagApiError(res.status, `feature-flags PUT failed: ${res.status}`);
  }
  return (await res.json()) as FeatureFlagMutationResult;
}

/** DELETE: 上書き行を削除＝既定に戻す + 監査。 */
export async function deleteWorkspaceFeatureFlag(
  baseUrl: string,
  workspaceId: string,
  key: string,
): Promise<FeatureFlagMutationResult> {
  const res = await fetch(
    `${trimUrl(baseUrl)}/api/v1/workspaces/${encodeURIComponent(workspaceId)}/feature-flags/${encodeURIComponent(key)}`,
    {
      method: 'DELETE',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    },
  );
  if (res.status === 403) {
    throw new FeatureFlagApiError(403, 'forbidden');
  }
  if (!res.ok) {
    throw new FeatureFlagApiError(res.status, `feature-flags DELETE failed: ${res.status}`);
  }
  return (await res.json()) as FeatureFlagMutationResult;
}
